import { z } from 'zod'
import { decimalSchema, positiveDecimalSchema, timestampSchema, uuidSchema } from './common.ts'
import { decimalFitsNumeric, warehouseUnitCodeSchema } from './units.ts'

const productNameSchema = z.string().trim().min(1).max(300)
const bomQuantitySchema = positiveDecimalSchema.refine(
  (value) => decimalFitsNumeric(value, 18, 3),
  'Кількість має відповідати формату numeric(18,3)',
)
const wasteFactorSchema = decimalSchema
  .refine(
    (value) => !value.startsWith('-') && (value.split('.', 1)[0] ?? '').replace(/^0+/, '') !== '',
    'Коефіцієнт відходів має бути не менше 1',
  )
  .refine(
    (value) => decimalFitsNumeric(value, 18, 3),
    'Коефіцієнт відходів має відповідати формату numeric(18,3)',
  )

export const createEngineeringProductInputSchema = z.strictObject({
  name: productNameSchema,
})
export type CreateEngineeringProductInput = z.infer<typeof createEngineeringProductInputSchema>

export const replaceTechnologyCardDraftLinesInputSchema = z.strictObject({
  lines: z
    .array(
      z.strictObject({
        materialId: uuidSchema,
        enteredQuantity: bomQuantitySchema,
        unitCode: warehouseUnitCodeSchema,
        wasteFactor: wasteFactorSchema,
        operation: z.string().trim().min(1).max(200).nullable().default(null),
      }),
    )
    .max(500),
})
export type ReplaceTechnologyCardDraftLinesInput = z.infer<
  typeof replaceTechnologyCardDraftLinesInputSchema
>

export const engineeringProductSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  archivedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})
export type EngineeringProduct = z.infer<typeof engineeringProductSchema>

export const technologyCardDraftLineSchema = z.object({
  id: uuidSchema,
  position: z.int().positive(),
  materialId: uuidSchema,
  enteredQuantity: decimalSchema,
  unitCode: warehouseUnitCodeSchema,
  wasteFactor: decimalSchema,
  operation: z.string().nullable(),
  materialName: z.string(),
  materialGrade: z.string().nullable(),
  materialCategory: z.string(),
  materialBaseUnit: warehouseUnitCodeSchema,
  materialBaseUnitSymbol: z.string(),
  materialDensityKgM3: decimalSchema.nullable(),
  materialSheetLengthMm: decimalSchema.nullable(),
  materialSheetWidthMm: decimalSchema.nullable(),
  materialThicknessMm: decimalSchema.nullable(),
  unitName: z.string(),
  unitSymbol: z.string(),
  unitPrecision: z.int(),
})
export type TechnologyCardDraftLine = z.infer<typeof technologyCardDraftLineSchema>

/** One stage of a technology card route, in shop order (prototype `TechCardVersion.route`). */
export const technologyCardRouteStageSchema = z.object({
  stageTemplateId: uuidSchema,
  position: z.int().positive(),
  key: z.string(),
  label: z.string(),
})
export type TechnologyCardRouteStage = z.infer<typeof technologyCardRouteStageSchema>

export const technologyCardDraftSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  /** What the next version changes and why. */
  note: z.string().nullable(),
  /**
   * Live labels; `archived` marks a stage that must be replaced before release. Empty means no
   * route recorded (new orders take every active stage), never "no stages".
   */
  route: z.array(technologyCardRouteStageSchema.extend({ archived: z.boolean() })),
  lines: z.array(technologyCardDraftLineSchema),
})
export type TechnologyCardDraft = z.infer<typeof technologyCardDraftSchema>

export const engineeringBomSummarySchema = z.object({
  materialLineCount: z.int().nonnegative(),
  distinctMaterialCount: z.int().nonnegative(),
  operationLineCount: z.int().nonnegative(),
  estimatedCurrentCostUah: decimalSchema,
  unavailableCostLineCount: z.int().nonnegative(),
})
export type EngineeringBomSummary = z.infer<typeof engineeringBomSummarySchema>

export const engineeringProductDraftSchema = z.object({
  product: engineeringProductSchema,
  draft: technologyCardDraftSchema,
  summary: engineeringBomSummarySchema,
})
export type EngineeringProductDraft = z.infer<typeof engineeringProductDraftSchema>

export const technologyCardVersionLineSchema = z.object({
  id: uuidSchema,
  position: z.int().positive(),
  materialId: uuidSchema,
  materialName: z.string(),
  materialGrade: z.string().nullable(),
  materialCategory: z.string(),
  materialBaseUnit: warehouseUnitCodeSchema,
  materialBaseUnitSymbol: z.string(),
  enteredQuantity: decimalSchema,
  enteredUnit: warehouseUnitCodeSchema,
  enteredUnitName: z.string(),
  enteredUnitSymbol: z.string(),
  enteredUnitPrecision: z.int(),
  conversionFactor: decimalSchema,
  baseQuantity: decimalSchema,
  wasteFactor: decimalSchema,
  requiredQuantity: decimalSchema,
  operation: z.string().nullable(),
  createdAt: timestampSchema,
})
export type TechnologyCardVersionLine = z.infer<typeof technologyCardVersionLineSchema>

export const technologyCardVersionSchema = z.object({
  id: uuidSchema,
  technologyCardId: uuidSchema,
  number: z.int().positive(),
  sourceDraftUpdatedAt: timestampSchema,
  releasedBy: z.string(),
  /** Null once the account is gone; the id stays. */
  releasedByName: z.string().nullable(),
  releasedAt: timestampSchema,
  note: z.string().nullable(),
  /**
   * Frozen at release with the labels it had then. Empty means no route recorded, whether the
   * version predates routes or was released without one: orders take every active stage.
   */
  route: z.array(technologyCardRouteStageSchema),
  lines: z.array(technologyCardVersionLineSchema),
})
export type TechnologyCardVersion = z.infer<typeof technologyCardVersionSchema>

/**
 * One catalog item as «Товари і техкарти» lists it: its released card, whether the draft holds
 * unreleased changes, and where it is in play. Internal orders name a product in free text, so they
 * cannot be counted against an item.
 */
export const engineeringCatalogEntrySchema = z.object({
  catalogItemId: uuidSchema,
  engineeringProductId: uuidSchema.nullable(),
  /** The card can no longer change; its released versions still stand. */
  engineeringProductArchived: z.boolean(),
  latestVersion: z
    .object({
      number: z.int().positive(),
      releasedAt: timestampSchema,
      lineCount: z.int().nonnegative(),
      /** Stage labels frozen with the version; empty when it recorded no route. */
      route: z.array(z.string()),
      /** One unit at today's moving-average stock prices, over lines that have stock. */
      materialCostUah: decimalSchema,
      unavailableCostLineCount: z.int().nonnegative(),
    })
    .nullable(),
  draftChanged: z.boolean(),
  usage: z.object({
    productionOrders: z.int().nonnegative(),
    openQuotes: z.int().nonnegative(),
  }),
})
export type EngineeringCatalogEntry = z.infer<typeof engineeringCatalogEntrySchema>

/**
 * «Де використовується»: running production orders and open quotes that carry the item. A line set
 * may name the item in more than one unit, so quantities come per unit and are never mixed.
 */
export const engineeringCatalogUsageSchema = z.object({
  productionOrders: z.array(
    z.object({
      orderId: uuidSchema,
      number: z.string(),
      clientName: z.string().nullable(),
      quantities: z.array(z.object({ quantity: decimalSchema, unitSymbol: z.string() })),
    }),
  ),
  openQuotes: z.array(
    z.object({
      dealId: uuidSchema,
      dealName: z.string(),
      clientName: z.string(),
      quantities: z.array(z.object({ quantity: decimalSchema, unit: z.string() })),
    }),
  ),
})
export type EngineeringCatalogUsage = z.infer<typeof engineeringCatalogUsageSchema>

export const replaceTechnologyCardDraftRouteInputSchema = z.strictObject({
  stageTemplateIds: z
    .array(uuidSchema)
    .max(50)
    .refine((ids) => new Set(ids).size === ids.length, 'Кожен етап можна додати лише один раз'),
})
export type ReplaceTechnologyCardDraftRouteInput = z.infer<
  typeof replaceTechnologyCardDraftRouteInputSchema
>

export const updateTechnologyCardDraftNoteInputSchema = z.strictObject({
  note: z.string().trim().max(2000).nullable(),
})
export type UpdateTechnologyCardDraftNoteInput = z.infer<
  typeof updateTechnologyCardDraftNoteInputSchema
>

export const handoffTechnologyCardVersionInputSchema = z.strictObject({
  orderItemId: uuidSchema,
})
export type HandoffTechnologyCardVersionInput = z.infer<
  typeof handoffTechnologyCardVersionInputSchema
>

export const ENGINEERING_PROJECT_STATUSES = [
  'sketch',
  'modeling',
  'drawing',
  'bom_approval',
  'review',
  'handed_off',
] as const

export const engineeringProjectStatusSchema = z.enum(ENGINEERING_PROJECT_STATUSES)
export type EngineeringProjectStatus = z.infer<typeof engineeringProjectStatusSchema>

export const updateEngineeringProjectStatusInputSchema = z.strictObject({
  status: z.enum(['sketch', 'modeling', 'drawing', 'bom_approval', 'review']),
})
export type UpdateEngineeringProjectStatusInput = z.infer<
  typeof updateEngineeringProjectStatusInputSchema
>

export const engineeringProjectStatusHistorySchema = z.object({
  id: uuidSchema,
  fromStatus: engineeringProjectStatusSchema.nullable(),
  toStatus: engineeringProjectStatusSchema,
  changedBy: z.string(),
  changedByName: z.string().nullable(),
  changedAt: timestampSchema,
})
export type EngineeringProjectStatusHistory = z.infer<typeof engineeringProjectStatusHistorySchema>

export const engineeringProjectSchema = z.object({
  id: uuidSchema,
  orderItemId: uuidSchema,
  orderId: uuidSchema,
  orderNumber: z.string(),
  orderKind: z.enum(['customer', 'internal']),
  clientName: z.string().nullable(),
  itemPosition: z.int().positive(),
  itemDescription: z.string(),
  quantity: z.string().nullable(),
  unitSymbol: z.string().nullable(),
  drawingCount: z.int().nonnegative(),
  routeCount: z.int().nonnegative(),
  bomLineCount: z.int().nonnegative(),
  engineeringProductId: uuidSchema.nullable(),
  engineeringProductName: z.string().nullable(),
  status: engineeringProjectStatusSchema,
  progressPercent: z.int().min(0).max(100),
  engineerId: z.string().nullable(),
  engineerName: z.string().nullable(),
  /** Who asked for the work; null on requests made before it was recorded. */
  requestedBy: z.string().nullable(),
  requestedByName: z.string().nullable(),
  /** When the requester needs it; the order's own deadline stays in `deadline`. */
  dueAt: timestampSchema.nullable(),
  note: z.string().nullable(),
  deadline: timestampSchema.nullable(),
  handedOffAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  fileCount: z.int().nonnegative(),
  latestBomVersion: z.int().positive().nullable(),
  handedOffBomVersion: z.int().positive().nullable(),
})
export type EngineeringProject = z.infer<typeof engineeringProjectSchema>

export const engineeringProjectDetailSchema = engineeringProjectSchema.extend({
  history: z.array(engineeringProjectStatusHistorySchema),
})
export type EngineeringProjectDetail = z.infer<typeof engineeringProjectDetailSchema>

export const updateEngineeringRequestInputSchema = z
  .strictObject({
    engineerId: z.string().trim().min(1).max(200).optional(),
    dueAt: timestampSchema.nullable().optional(),
    note: z.string().trim().max(2000).nullable().optional(),
  })
  .refine(
    (input) =>
      input.dueAt !== undefined || input.note !== undefined || input.engineerId !== undefined,
    {
      message: 'Немає змін',
    },
  )
export type UpdateEngineeringRequestInput = z.infer<typeof updateEngineeringRequestInputSchema>

export const assignEngineeringProjectInputSchema = z.strictObject({
  engineerId: z.string().trim().min(1).max(200),
})
export type AssignEngineeringProjectInput = z.infer<typeof assignEngineeringProjectInputSchema>

export const assignableEngineerSchema = z.object({ id: z.string(), name: z.string() })
export type AssignableEngineer = z.infer<typeof assignableEngineerSchema>
